import { backofficeFetch } from '@/services/backoffice-api';
import type {
  BackofficeAssignableUserSearchResponse,
  BackofficeInterviewRecordingResponse,
  BackofficeOrganizationSearchResponse,
  BackofficeInterviewsResponse,
  BackofficeProjectDetailResponse,
  BackofficeProjectDetail,
  BackofficeProjectInterviewsResponse,
  BackofficeProjectTargetScopeResponse,
  BackofficeProjectsResponse,
  UpdateBackofficeProjectParticipantPrivacyInput,
  UpdateBackofficeProjectAssignmentInput,
  BackofficeUserLookupResponse,
} from './types';

export async function fetchBackofficeProjects(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeProjectsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/projects?${searchParams.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load studies (${response.status})`);
  }

  return (await response.json()) as BackofficeProjectsResponse;
}

export async function fetchBackofficeUserByEmail(
  email: string,
  signal?: AbortSignal
): Promise<BackofficeUserLookupResponse | null> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({ email });
  const response = await backofficeFetch(`/users/lookup?${params.toString()}`, init);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to resolve owner (${response.status})`);
  }

  return (await response.json()) as BackofficeUserLookupResponse;
}

export async function fetchBackofficeProjectDetail(
  projectId: string,
  signal?: AbortSignal
): Promise<BackofficeProjectDetailResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/projects/${encodeURIComponent(projectId)}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load study (${response.status})`);
  }

  return (await response.json()) as BackofficeProjectDetailResponse;
}

export async function fetchBackofficeProjectTargetScope(
  projectId: string,
  signal?: AbortSignal
): Promise<BackofficeProjectTargetScopeResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/target-scope`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load target and scope (${response.status})`);
  }

  return (await response.json()) as BackofficeProjectTargetScopeResponse;
}

export async function searchBackofficeProjectOrganizations(
  query: string,
  signal?: AbortSignal
): Promise<BackofficeOrganizationSearchResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({
    search: query,
    sortBy: 'name',
    sortDirection: 'asc',
    limit: '10',
    offset: '0',
  });
  const response = await backofficeFetch(`/organizations?${params.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to search organizations (${response.status})`);
  }

  return (await response.json()) as BackofficeOrganizationSearchResponse;
}

export async function searchBackofficeProjectOwners(
  organizationId: string,
  query: string,
  signal?: AbortSignal
): Promise<BackofficeAssignableUserSearchResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const params = new URLSearchParams({
    organizationId,
    search: query,
    sortBy: 'name',
    sortDirection: 'asc',
    limit: '10',
    offset: '0',
  });
  const response = await backofficeFetch(`/users?${params.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to search owners (${response.status})`);
  }

  return (await response.json()) as BackofficeAssignableUserSearchResponse;
}

export async function updateBackofficeProjectAssignment(
  projectId: string,
  input: UpdateBackofficeProjectAssignmentInput
): Promise<BackofficeProjectDetailResponse> {
  const response = await backofficeFetch(`/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update study ownership (${response.status})`);
  }

  return body as BackofficeProjectDetailResponse;
}

export async function updateBackofficeProjectParticipantPrivacy(
  projectId: string,
  input: UpdateBackofficeProjectParticipantPrivacyInput
): Promise<BackofficeProjectDetailResponse> {
  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/participant-privacy`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update participant privacy (${response.status})`);
  }

  return body as BackofficeProjectDetailResponse;
}

export async function updateBackofficeProjectProductLine(
  projectId: string,
  input: { productLine: NonNullable<BackofficeProjectDetail['productLine']> }
): Promise<BackofficeProjectDetailResponse> {
  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/product-line`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update product line (${response.status})`);
  }

  return body as BackofficeProjectDetailResponse;
}

export async function fetchBackofficeProjectInterviews(
  projectId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeProjectInterviewsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/interviews?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load interviews (${response.status})`);
  }

  return (await response.json()) as BackofficeProjectInterviewsResponse;
}

export async function fetchBackofficeInterviews(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeInterviewsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/interviews?${searchParams.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load interviews (${response.status})`);
  }

  return (await response.json()) as BackofficeInterviewsResponse;
}

export async function fetchBackofficeInterviewRecording(
  interviewId: string,
  signal?: AbortSignal
): Promise<BackofficeInterviewRecordingResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/interviews/${encodeURIComponent(interviewId)}/recording`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load interview recording (${response.status})`);
  }

  return (await response.json()) as BackofficeInterviewRecordingResponse;
}

export async function fetchBackofficeInterviewLink(
  projectId: string,
  signal?: AbortSignal
): Promise<import('@nava/backoffice-bff-contracts').BackofficeInterviewLink> {
  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/interview-link`,
    {
      method: 'GET',
      ...(signal ? { signal } : {}),
    }
  );
  if (!response.ok) throw new Error('Interview link is temporarily unavailable');
  return response.json();
}

export async function updateBackofficeInterviewLink(
  projectId: string,
  hostname: string
): Promise<import('@nava/backoffice-bff-contracts').BackofficeInterviewLink> {
  const response = await backofficeFetch(
    `/projects/${encodeURIComponent(projectId)}/interview-link`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostname }),
    }
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Failed to update interview link');
  }
  return response.json();
}
